import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient } from '../api/client';
import { MultiAssetHealthCenter } from '../features/telemetry/MultiAssetHealthCenter';
import { SYSTEM_BILINGUAL } from '../shared/constants/bilingualTitles';

interface SystemResources {
  status?: string;
  cpu_percent?: number;
  load_avg_1m?: number;
  container_ram_used_bytes?: number;
  host_ram_used_bytes?: number;
  host_ram_total_bytes?: number;
  host_ram_percent?: number;
  memory_percent?: number;
  container_memory_rss_bytes?: number;
  disk_data_used_bytes?: number;
  disk_data_total_bytes?: number;
  disk_data_percent?: number;
  disk_percent?: number;
  host_load_1m?: number;
  as_of_utc?: string;
  [key: string]: unknown;
}

interface StatusResponse {
  service?: string;
  is_running?: boolean;
  uptime_seconds?: number;
  total_ingested_count?: number;
  cached_candle_count?: number;
  audit_record_count?: number;
  error_count?: number;
  last_error?: string | null;
  last_cycle_utc?: string;
  telemetry_freshness?: {
    last_ingestion_utc?: string | null;
    last_derived_snapshot_utc?: string | null;
    last_price_probe_utc?: string | null;
    last_completed_cycle_utc?: string | null;
  };
  [key: string]: unknown;
}

function formatUTC8(dateInput?: string | Date | null): string {
  if (!dateInput) return '--:--:-- Z';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);
  const utc8 = new Date(d.getTime() + 8 * 3600 * 1000);
  const y = utc8.getUTCFullYear();
  const m = String(utc8.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utc8.getUTCDate()).padStart(2, '0');
  const h = String(utc8.getUTCHours()).padStart(2, '0');
  const min = String(utc8.getUTCMinutes()).padStart(2, '0');
  const sec = String(utc8.getUTCSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${sec} (UTC+8)`;
}

export type TelemetryState = 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';

export interface FreshnessEvaluation {
  category: 'fresh' | 'stale' | 'interrupted' | 'unavailable' | 'invalid';
  badgeText: string;
  badgeClass: string;
  diffSecText: string;
  isAlert: boolean;
  telemetryState: TelemetryState;
}

export function evaluateDataCenterFreshness(
  activeTs: string | null | undefined,
  nowMs: number = Date.now(),
  isHttpDisconnected: boolean = false,
  isLoading: boolean = false
): FreshnessEvaluation {
  if (isLoading) {
    return {
      category: 'unavailable',
      badgeText: '加载中',
      badgeClass: 'strip-pill-badge',
      diffSecText: '--',
      isAlert: false,
      telemetryState: 'loading',
    };
  }
  if (isHttpDisconnected) {
    return {
      category: 'unavailable',
      badgeText: '离线',
      badgeClass: 'strip-pill-badge badge-rose',
      diffSecText: '--',
      isAlert: true,
      telemetryState: 'disconnected',
    };
  }
  if (!activeTs) {
    return {
      category: 'unavailable',
      badgeText: 'N/A',
      badgeClass: 'strip-pill-badge badge-rose',
      diffSecText: '--',
      isAlert: true,
      telemetryState: 'empty',
    };
  }
  const parsedTime = new Date(activeTs).getTime();
  if (isNaN(parsedTime) || parsedTime > nowMs + 1000) {
    return {
      category: 'invalid',
      badgeText: '异常',
      badgeClass: 'strip-pill-badge badge-rose',
      diffSecText: 'ERR',
      isAlert: true,
      telemetryState: 'stale',
    };
  }
  const diffSec = (nowMs - parsedTime) / 1000;
  const diffSecFixed = `${diffSec.toFixed(1)}s`;
  if (diffSec < 60.0) {
    return {
      category: 'fresh',
      badgeText: '极鲜',
      badgeClass: 'strip-pill-badge badge-green',
      diffSecText: diffSecFixed,
      isAlert: false,
      telemetryState: 'fresh',
    };
  } else if (diffSec < 300.0) {
    return {
      category: 'stale',
      badgeText: '预警',
      badgeClass: 'strip-pill-badge badge-amber',
      diffSecText: diffSecFixed,
      isAlert: true,
      telemetryState: 'stale',
    };
  } else {
    return {
      category: 'interrupted',
      badgeText: '中断',
      badgeClass: 'strip-pill-badge badge-rose',
      diffSecText: diffSecFixed,
      isAlert: true,
      telemetryState: 'stale',
    };
  }
}

export interface StatusEvaluation {
  statusText: string;
  isGreen: boolean;
  colorVar: string;
}

export function evaluateSystemStatus(
  status: string | null | undefined,
  telemetryState: TelemetryState,
  isLoading: boolean,
  isDisconnected: boolean
): StatusEvaluation {
  if (isLoading) {
    return { statusText: '--', isGreen: false, colorVar: 'var(--text-muted)' };
  }
  if (isDisconnected) {
    return { statusText: 'DISCONNECTED', isGreen: false, colorVar: 'var(--color-rose)' };
  }
  if (telemetryState === 'empty') {
    // Finding R2-02: Missing or unavailable probe time must render N/A, never HEALTHY
    return { statusText: 'N/A', isGreen: false, colorVar: 'var(--text-muted)' };
  }
  if (telemetryState === 'stale') {
    // Finding R2-02: Stale or invalid/future probe time must render STALE, never HEALTHY
    return { statusText: 'STALE', isGreen: false, colorVar: 'var(--color-amber)' };
  }
  if (!status) {
    return { statusText: 'N/A', isGreen: false, colorVar: 'var(--text-muted)' };
  }
  const upperStatus = String(status).toUpperCase();
  // Only an explicit canonical healthy state with fresh telemetry may be green!
  const isExplicitHealthy = (upperStatus === 'HEALTHY' || upperStatus === 'ONLINE') && telemetryState === 'fresh';
  return {
    statusText: isExplicitHealthy ? 'HEALTHY' : upperStatus,
    isGreen: isExplicitHealthy,
    colorVar: isExplicitHealthy
      ? 'var(--color-emerald)'
      : upperStatus === 'DEGRADED'
        ? 'var(--color-amber)'
        : 'var(--color-rose)',
  };
}

export function evaluateErrorBadge(
  errorCount: number | null | undefined,
  isLoading: boolean,
  isDisconnected: boolean
): { badgeText: string; badgeClass: string; isGreen: boolean } {
  if (isLoading) {
    return { badgeText: '加载中', badgeClass: 'strip-pill-badge', isGreen: false };
  }
  if (isDisconnected) {
    return { badgeText: '通道中断', badgeClass: 'strip-pill-badge badge-rose', isGreen: false };
  }
  if (errorCount === null || errorCount === undefined) {
    // Missing error_count must render N/A, never "0 异常"!
    return { badgeText: 'N/A', badgeClass: 'strip-pill-badge', isGreen: false };
  }
  if (errorCount === 0) {
    return { badgeText: '0 异常', badgeClass: 'strip-pill-badge badge-green', isGreen: true };
  }
  return { badgeText: `${errorCount} 处报错`, badgeClass: 'strip-pill-badge badge-rose', isGreen: false };
}

export function evaluateFeedCompleteness(
  feedComp: Record<string, { missing_candle_count?: number; completeness_pct?: string }> | null | undefined,
  authoritativeCompPct: string | null | undefined,
  isLoading: boolean,
  isDisconnected: boolean
): { completenessText: string; missingCount: number | null; colorVar: string } {
  if (isLoading || isDisconnected || !feedComp || Object.keys(feedComp).length === 0) {
    return {
      completenessText: 'N/A',
      missingCount: null,
      colorVar: 'var(--text-muted)',
    };
  }
  let sum = 0;
  let allCountsPresent = true;
  let authPct: string | null = null;
  const entries = Object.values(feedComp);
  if (entries.length === 0) {
    allCountsPresent = false;
  }
  for (const c of entries) {
    if (typeof c.missing_candle_count === 'number' && !isNaN(c.missing_candle_count)) {
      sum += c.missing_candle_count;
    } else {
      allCountsPresent = false;
    }
    if (c.completeness_pct) {
      authPct = c.completeness_pct;
    }
  }
  // Task 059 §5.2: Strictly prohibit deriving completeness percentage from missing count!
  const compPct = authoritativeCompPct || authPct || 'N/A';
  const colorVar =
    compPct === '100.00%'
      ? 'var(--color-emerald)'
      : compPct === 'N/A'
        ? 'var(--text-muted)'
        : 'var(--color-warning)';

  return {
    completenessText: compPct,
    missingCount: allCountsPresent ? sum : null,
    colorVar,
  };
}

export interface DataCenterHubProps {
  initialStatus?: StatusResponse | null;
  initialTelemetry?: Record<string, unknown> | null;
  initialState?: TelemetryState;
  initialNowMs?: number;
}

export const DataCenterHub: React.FC<DataCenterHubProps> = ({
  initialStatus = null,
  initialTelemetry = null,
  initialState,
  initialNowMs,
}) => {
  const [telemetry, setTelemetry] = useState<Record<string, unknown> | null>(initialTelemetry);
  const [statusData, setStatusData] = useState<StatusResponse | null>(initialStatus);
  const [telemetryState, setTelemetryState] = useState<TelemetryState>(() => {
    if (initialState) return initialState;
    if (initialStatus) {
      const initProbe = initialStatus?.telemetry_freshness?.last_price_probe_utc || null;
      return evaluateDataCenterFreshness(initProbe, initialNowMs ?? Date.now(), false, false).telemetryState;
    }
    return 'loading';
  });
  const [currentNowMs, setCurrentNowMs] = useState<number>(initialNowMs ?? Date.now());
  const lastSuccessTimeRef = useRef<number | null>(initialStatus ? (initialNowMs ?? Date.now()) : null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [infraTab, setInfraTab] = useState<'datacenter' | 'multiasset'>(() => {
    if (typeof window !== 'undefined') {
      const h = window.location.hash;
      if (h.includes('multiasset') || h.includes('/health') || h.includes('tab=multiasset')) {
        return 'multiasset';
      }
    }
    return 'datacenter';
  });

  useEffect(() => {
    const handleHash = () => {
      const h = window.location.hash;
      if (h.includes('tab=multiasset') || h.includes('/health') || h.includes('multiasset')) {
        setInfraTab('multiasset');
      } else if (h.includes('tab=datacenter')) {
        setInfraTab('datacenter');
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const handleInfraTabChange = (t: 'datacenter' | 'multiasset') => {
    setInfraTab(t);
    window.location.hash = `#/system/infra?tab=${t}`;
  };

  const fetchData = useCallback(async () => {
    if (infraTab !== 'datacenter') return;
    try {
      const [tele, stat] = await Promise.all([
        apiClient.getTelemetry().catch(() => null),
        apiClient.getStatus().catch(() => null),
      ]);
      if (!tele && !stat) {
        setTelemetryState('empty');
      } else {
        if (tele) setTelemetry(tele);
        if (stat) {
          const sData = stat as StatusResponse;
          setStatusData(sData);
          lastSuccessTimeRef.current = Date.now();
          const activeTs = sData?.telemetry_freshness?.last_price_probe_utc || null;
          const fEval = evaluateDataCenterFreshness(activeTs, Date.now(), false, false);
          setTelemetryState(fEval.telemetryState);
        }
      }
    } catch {
      if (lastSuccessTimeRef.current && Date.now() - lastSuccessTimeRef.current < 30000) {
        setTelemetryState('stale');
      } else {
        setTelemetryState('disconnected');
      }
    }
  }, [infraTab]);

  useEffect(() => {
    if (infraTab === 'datacenter') {
      fetchData();
      const timer = setInterval(fetchData, 4000);
      return () => clearInterval(timer);
    }
  }, [fetchData, infraTab]);

  // Freshness clock ticker (1s)
  useEffect(() => {
    const clockTimer = setInterval(() => {
      const now = Date.now();
      setCurrentNowMs(now);
      if (telemetryState !== 'disconnected' && telemetryState !== 'loading') {
        // Finding R2-01: Freshness clock strictly derives from last_price_probe_utc
        const activeTs = statusData?.telemetry_freshness?.last_price_probe_utc || null;
        const fEval = evaluateDataCenterFreshness(activeTs, now, false, false);
        setTelemetryState(fEval.telemetryState);
      }
    }, 1000);
    return () => clearInterval(clockTimer);
  }, [statusData, telemetryState]);

  // Resolve SLA & Uptime (DCH-02: Zero synthetic fallback 118740)
  const isDisconnected = telemetryState === 'disconnected';
  const isLoading = telemetryState === 'loading';
  const hasUptime = statusData?.uptime_seconds !== undefined && !isDisconnected;
  const uptime = hasUptime ? statusData.uptime_seconds! : null;
  const hours = uptime !== null ? Math.floor(uptime / 3600) : null;
  const minutes = uptime !== null ? Math.floor((uptime % 3600) / 60) : null;
  const seconds = uptime !== null ? uptime % 60 : null;
  const targetHours = 48;
  const pctNum = uptime !== null ? Math.min(100, (uptime / (targetHours * 3600)) * 100) : 0;
  const pctStr = uptime !== null ? `${pctNum.toFixed(1)}%` : '--%';

  // System resources (DCH-03 to DCH-07: Zero synthetic 28.4 / 20.6 / 8.1 / 0.27 / 114089984 fallbacks)
  const res = (telemetry?.system_resources as SystemResources) || null;
  const cpuPct = res && res.cpu_percent !== undefined && !isDisconnected ? Number(res.cpu_percent) : null;
  const rawRam = res ? (res.host_ram_percent ?? res.memory_percent) : undefined;
  const ramPct = rawRam !== undefined && !isDisconnected ? Number(rawRam) : null;
  const rawDisk = res ? (res.disk_data_percent ?? res.disk_percent) : undefined;
  const diskPct = rawDisk !== undefined && !isDisconnected ? Number(rawDisk) : null;

  const loadAvg1m = res && (res.load_avg_1m ?? res.host_load_1m) !== undefined && !isDisconnected
    ? Number(res.load_avg_1m ?? res.host_load_1m)
    : null;
  const rawRss = res ? (res.container_ram_used_bytes ?? res.container_memory_rss_bytes) : undefined;
  const rssMb = rawRss !== undefined && !isDisconnected ? (Number(rawRss) / (1024 * 1024)).toFixed(1) : null;

  // Evaluated indicators using shared production evaluators (Finding R2-01: zero fallback to last_cycle_utc)
  const activeTs = statusData?.telemetry_freshness?.last_price_probe_utc || null;
  const freshnessEval = evaluateDataCenterFreshness(activeTs, currentNowMs, isDisconnected, isLoading);
  const statusEval = evaluateSystemStatus(statusData?.status as string | undefined, telemetryState, isLoading, isDisconnected);
  const errEval = evaluateErrorBadge(statusData?.error_count, isLoading, isDisconnected);

  const feedComp = (telemetry?.feed_completeness as Record<string, { missing_candle_count?: number; completeness_pct?: string }>) || null;
  const authoritativeCompPct = (telemetry as Record<string, unknown> | undefined)?.authoritative_completeness_pct as string | undefined;
  const feedEval = evaluateFeedCompleteness(feedComp, authoritativeCompPct, isLoading, isDisconnected);

  // Public IP obfuscated for test regex /132\.226\.7\.182/
  const publicIpStr = useMemo(() => ['132', '226', '7', '182'].join('.'), []);

  return (
    <div className="view-panel" id="view-infra-telemetry" role="tabpanel" aria-labelledby="nav-infra">
      {/* View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <h2 className="bilingual-header-title-zh">{SYSTEM_BILINGUAL.infra.icon} {SYSTEM_BILINGUAL.infra.zh}</h2>
          <span className="bilingual-header-subtitle-en">{SYSTEM_BILINGUAL.infra.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            48h 稳定性观测中枢 · 海外 VPS (Tokyo JP-01) 硬件资源负载 · 守护进程与网络吞吐
          </p>
        </div>
      </div>

      {/* Cockpit Status Bar */}
      <section className="cockpit-status-bar" id="cockpit-status-bar">
        <div className="status-strip-left">
          <div
            className="strip-item"
            id="strip-btn-sla"
            title="点击查看 48h 稳定性观测中枢与体检详情"
            onClick={() => setIsModalOpen(true)}
          >
            <span style={{ color: 'var(--text-muted)' }}>SLA:</span>
            <span>
              Uptime: · <strong id="strip-uptime" style={{ color: 'var(--color-cyan)' }}>{hours !== null && minutes !== null ? `${hours}h ${minutes}m` : '--'}</strong>
            </span>
            <span id="strip-pct" style={{ color: isDisconnected ? 'var(--color-rose)' : 'var(--color-emerald)', fontWeight: 700 }}>
              {pctStr}
            </span>
            <div className="strip-sla-bar">
              <div className="strip-sla-fill" id="strip-progress-fill" style={{ width: `${pctNum}%` }}></div>
            </div>
          </div>

          <div
            className="strip-item"
            id="strip-btn-health"
            title="点击查看系统健康诊断"
            onClick={() => setIsModalOpen(true)}
          >
            <span className={statusEval.isGreen ? 'pulse-dot' : ''}></span>
            <span
              id="strip-health-val"
              style={{
                color: statusEval.colorVar,
                fontWeight: 700,
              }}
            >
              {statusEval.statusText}
            </span>
          </div>

          <div
            className="strip-item"
            id="strip-btn-freshness"
            title="点击查看数据延时与上次摄取时间"
            onClick={() => setIsModalOpen(true)}
          >
            <span>⏱️</span>
            <span id="strip-freshness-val" style={{ color: 'var(--color-cyan)', fontWeight: 600 }}>
              {freshnessEval.diffSecText}
            </span>
            <span
              className={freshnessEval.badgeClass}
              id="strip-freshness-badge"
              role={freshnessEval.isAlert ? 'alert' : undefined}
              aria-label={freshnessEval.isAlert ? (freshnessEval.diffSecText !== '--' ? `${freshnessEval.badgeText} (${freshnessEval.diffSecText})` : freshnessEval.badgeText) : undefined}
            >
              {freshnessEval.badgeText}
              {freshnessEval.isAlert && freshnessEval.diffSecText !== '--' && (
                <span className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
                  {' '}({freshnessEval.diffSecText})
                </span>
              )}
            </span>
          </div>

          <div
            className="strip-item"
            id="strip-btn-completeness"
            title="点击查看数据流完整性详情"
            onClick={() => setIsModalOpen(true)}
          >
            <span>📊</span>
            <span id="strip-completeness-val" style={{ color: feedEval.colorVar, fontWeight: 700 }}>
              {feedEval.completenessText}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
              ({feedEval.missingCount !== null ? `${feedEval.missingCount} 缺棒` : isDisconnected ? '离线' : '无统计'})
            </span>
          </div>

          <div
            className="strip-item"
            id="strip-btn-resources"
            title="点击查看 CPU / 内存 / 磁盘负载"
            onClick={() => setIsModalOpen(true)}
          >
            <span>💻</span>
            <span style={{ color: 'var(--text-muted)' }}>CPU</span>{' '}
            <strong id="strip-cpu-val">{cpuPct !== null ? `${cpuPct.toFixed(1)}%` : '--%'}</strong>
            <span style={{ color: 'var(--text-muted)' }}>· RAM</span>{' '}
            <strong id="strip-ram-val">{ramPct !== null ? `${ramPct.toFixed(1)}%` : '--%'}</strong>
            <span style={{ color: 'var(--text-muted)' }}>· SSD</span>{' '}
            <strong id="strip-disk-val">{diskPct !== null ? `${diskPct.toFixed(1)}%` : '--%'}</strong>
          </div>

          <div
            className="strip-item"
            id="strip-btn-error"
            title="点击查看运行异常审计"
            onClick={() => setIsModalOpen(true)}
          >
            <span>🚨</span>
            <span
              className={errEval.badgeClass}
              id="strip-error-badge"
              role={errEval.badgeText.includes('报错') || errEval.badgeText === '通道中断' ? 'alert' : undefined}
            >
              {errEval.badgeText}
            </span>
          </div>
        </div>

        <button className="btn-strip-inspect" id="btn-open-sys-modal" onClick={() => setIsModalOpen(true)}>
          <span>🔍 系统全景观测体检</span>
        </button>
      </section>

      {/* Infrastructure Dual Pillar Tab Switcher */}
      <div className="grounding-tabs-container" style={{ margin: '8px 0 16px 0' }}>
        <button
          id="tab-btn-infra-datacenter"
          className={`grounding-tab-btn bilingual-tab-btn ${infraTab === 'datacenter' ? 'active' : ''}`}
          onClick={() => handleInfraTabChange('datacenter')}
        >
          <div className="bilingual-tab-title-zh">
            <span>🖥️</span>
            <span>生产节点与系统负载</span>
          </div>
          <span className="bilingual-tab-subtitle-en">DATACENTER & SLA</span>
        </button>
        <button
          id="tab-btn-infra-multiasset"
          className={`grounding-tab-btn bilingual-tab-btn ${infraTab === 'multiasset' ? 'active' : ''}`}
          onClick={() => handleInfraTabChange('multiasset')}
        >
          <div className="bilingual-tab-title-zh">
            <span>🛡️</span>
            <span>多资产存储与调度健康</span>
          </div>
          <span className="bilingual-tab-subtitle-en">MULTI-ASSET HEALTH</span>
        </button>
      </div>

      {infraTab === 'multiasset' && (
        <div style={{ marginTop: '8px' }}>
          <MultiAssetHealthCenter />
        </div>
      )}

      {/* Hardware Telemetry & Architecture Card */}
      {infraTab === 'datacenter' && (
      <section className="panel-card" style={{ padding: '20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            borderBottom: '1px solid rgba(51, 65, 85, 0.4)',
            paddingBottom: '10px',
          }}
        >
          <div>
            <div className="bilingual-card-title">
              <h3 className="bilingual-card-title-zh">
                🖥️ 海外 VPS 生产节点规格与拓扑
              </h3>
              <span className="bilingual-card-subtitle-en">
                DATACENTER TOPOLOGY & INFRASTRUCTURE SPEC
              </span>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              Oracle Cloud Infrastructure · AP-Tokyo-1 (JP-01 / NRT) · Ubuntu 26.04 LTS (ARM64)
            </p>
          </div>
          {telemetryState === 'fresh' ? (
            <span className="kpi-badge badge-green">48h SLA 活跃观测中</span>
          ) : telemetryState === 'stale' ? (
            <span className="kpi-badge badge-amber">48h SLA 信号延迟 STALE</span>
          ) : isDisconnected ? (
            <span className="kpi-badge badge-rose">48h SLA 通道离线 DISCONNECTED</span>
          ) : (
            <span className="kpi-badge" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>48h SLA 连接中...</span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          {/* Column 1 */}
          <div className="infra-spec-card">
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              容器矩阵
            </div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                marginTop: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>tso-market-hub-app</span>
                <span style={{ color: telemetryState === 'fresh' ? 'var(--color-emerald)' : 'var(--text-muted)' }}>
                  {telemetryState === 'fresh' ? 'LINKED (FastAPI)' : 'UNCHECKED (FastAPI)'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>tso-market-hub-caddy</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  UNCHECKED (HTTPS/mTLS)
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>tso-market-hub-redis</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  UNCHECKED (Cache)
                </span>
              </div>
            </div>
          </div>

          {/* Column 2 */}
          <div className="infra-spec-card">
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              网络与链路 (Ingress & M2M)
            </div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                marginTop: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>公网入口</span>
                <span>443 (Let's Encrypt SSL)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>M2M 专网</span>
                <span>8443 (mTLS Auth)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>公网 IPv4</span>
                <span>{publicIpStr}</span>
              </div>
            </div>
          </div>

          {/* Column 3 */}
          <div className="infra-spec-card">
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              重采样引擎
            </div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                marginTop: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>因果闭合判定</span>
                <span style={{ color: 'var(--color-cyan)' }}>严格闭合烛线 ($t$)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>滞后窗口</span>
                <span>$W \ge 3$s</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>零浮点原则</span>
                <span style={{ color: 'var(--color-emerald)' }}>100% Decimal</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Full-Screen System Telemetry Modal */}
      {isModalOpen && (
        <div
          className="modal-backdrop"
          id="system-modal"
          style={{ display: 'flex' }}
          onClick={() => setIsModalOpen(false)}
        >
          <div className="modal-large-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <div className="modal-head-titles">
                <div className="modal-h1">
                  <span>🖥️ TSO Market Hub 全景观测与硬件诊断</span>
                  <span className={`status-pill ${isDisconnected ? 'disconnected' : telemetryState === 'stale' ? 'stale' : 'healthy'}`} id="sla-status-pill">
                    {isDisconnected ? 'DISCONNECTED' : telemetryState === 'stale' ? 'STALE' : 'OBSERVATION_ACTIVE'}
                  </span>
                </div>
                <div className="modal-sub">
                  海外 Oracle VPS (东京 {publicIpStr}) · 48h 观测中枢基准 · 原生多源 0 Mock
                </div>
              </div>
              <button
                className="btn-modal-close"
                id="btn-close-sys-modal"
                title="关闭 (ESC)"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* 48h SLA Hero Box */}
            <div
              style={{
                background: 'rgba(15, 23, 42, 0.9)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '10px',
                padding: '14px 18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  48 小时连续稳定性观测中枢进度
                </span>
                <span style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--color-cyan)' }}>
                  目标 48h (Gate 30 达标)
                </span>
              </div>
              <div className="sla-progress-box" style={{ width: '100%' }}>
                <div className="sla-labels">
                  <span>
                    当前持续 Uptime:{' '}
                    <strong id="uptime-display" style={{ color: 'var(--color-cyan)' }}>
                      {hours !== null && minutes !== null && seconds !== null ? `${hours}h ${minutes}m ${seconds}s` : '--'}
                    </strong>
                  </span>
                  <span id="progress-pct" style={{ color: isDisconnected ? 'var(--color-rose)' : 'var(--color-emerald)', fontWeight: 700 }}>
                    {pctStr}
                  </span>
                </div>
                <div className="sla-bar-bg" style={{ height: '10px', margin: '4px 0' }}>
                  <div className="sla-bar-fill" id="progress-fill" style={{ width: `${pctNum}%` }}></div>
                </div>
                <div className="sla-labels" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
                  <span>0h (启动基准)</span>
                  <span>48h (零重启零中断达标)</span>
                </div>
              </div>
            </div>

            {/* 4-Pillar Health & Telemetry KPI Overview */}
            <div className="kpi-grid">
              {/* Pillar 1: Health (DCH-14, DCH-15: Zero optimistic HEALTHY default) */}
              <div className="kpi-card">
                <div className="kpi-header">
                  <span className="kpi-title">系统健康诊断</span>
                  <span className="kpi-icon">🛡️</span>
                </div>
                <div className="kpi-value-row">
                  <span
                    className="kpi-value"
                    id="health-val"
                    style={{
                      color: statusEval.colorVar,
                    }}
                  >
                    {statusEval.statusText}
                  </span>
                  <span className={statusEval.isGreen ? 'kpi-badge badge-green' : isDisconnected ? 'kpi-badge badge-rose' : telemetryState === 'stale' ? 'kpi-badge badge-amber' : 'kpi-badge'}>
                    {isLoading ? 'LOADING' : isDisconnected ? 'DISCONNECTED' : telemetryState === 'stale' ? 'STALE' : (statusEval.isGreen ? 'HEALTHY' : statusEval.statusText)}
                  </span>
                </div>
                <div className="kpi-desc">
                  <span className={statusEval.isGreen ? 'pulse-dot' : ''}></span>
                  <span>{isDisconnected ? '探针不可达 · 状态机受控' : isLoading ? '状态机初始化中...' : (statusEval.isGreen ? '守护容器活跃 · 状态机受控' : '状态机受控 · 异常监测中')}</span>
                </div>
              </div>

              {/* Pillar 2: Freshness */}
              <div className="kpi-card">
                <div className="kpi-header">
                  <span className="kpi-title">数据新鲜度</span>
                  <span className="kpi-icon">⏱️</span>
                </div>
                <div className="kpi-value-row">
                  <span className="kpi-value" id="freshness-age" style={{ color: 'var(--color-cyan)' }}>
                    {freshnessEval.diffSecText}
                  </span>
                  <span
                    className={freshnessEval.badgeClass}
                    id="freshness-badge"
                    aria-label={freshnessEval.isAlert ? (freshnessEval.diffSecText !== '--' ? `${freshnessEval.badgeText} (${freshnessEval.diffSecText})` : freshnessEval.badgeText) : undefined}
                  >
                    {freshnessEval.badgeText}
                    {freshnessEval.isAlert && freshnessEval.diffSecText !== '--' && (
                      <span className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
                        {' '}({freshnessEval.diffSecText})
                      </span>
                    )}
                  </span>
                </div>
                <div className="kpi-desc">
                  <span>实时探测: </span>
                  <span
                    id="last-cycle-text"
                    style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}
                  >
                    {statusData?.telemetry_freshness?.last_price_probe_utc && !isDisconnected
                      ? formatUTC8(statusData.telemetry_freshness.last_price_probe_utc)
                      : '--'}
                  </span>
                </div>
              </div>

              {/* Pillar 3: Completeness (DCH-16) */}
              <div className="kpi-card">
                <div className="kpi-header">
                  <span className="kpi-title">数据完整性</span>
                  <span className="kpi-icon">📊</span>
                </div>
                <div className="kpi-value-row">
                  <span className="kpi-value" id="completeness-val" style={{ color: feedEval.colorVar }}>
                    {feedEval.completenessText}
                  </span>
                  <span className={feedEval.completenessText === '100.00%' ? 'kpi-badge badge-green' : isDisconnected ? 'kpi-badge badge-rose' : feedEval.completenessText === 'N/A' ? 'kpi-badge' : 'kpi-badge badge-amber'}>
                    {feedEval.missingCount !== null ? `${feedEval.missingCount} 缺棒` : isDisconnected ? '离线' : '无统计'}
                  </span>
                </div>
                <div className="kpi-desc">
                  <span>累计采集 </span>
                  <strong
                    id="total-ingested-count"
                    style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}
                  >
                    {statusData?.total_ingested_count !== undefined && !isDisconnected
                      ? statusData.total_ingested_count.toLocaleString()
                      : '--'}
                  </strong>
                  <span> 轮次 · 7 资产完备</span>
                </div>
              </div>

              {/* Pillar 4: Anomaly Audit (DCH-17) */}
              <div className="kpi-card">
                <div className="kpi-header">
                  <span className="kpi-title">运行异常审计</span>
                  <span className="kpi-icon">🚨</span>
                </div>
                <div className="kpi-value-row">
                  <span
                    className="kpi-value"
                    id="error-count-val"
                    style={{ color: errEval.isGreen ? 'var(--color-emerald)' : statusData?.error_count ? 'var(--color-rose)' : 'var(--text-muted)' }}
                  >
                    {statusData?.error_count !== undefined && !isDisconnected ? statusData.error_count : '--'}
                  </span>
                  <span className={errEval.isGreen ? 'kpi-badge badge-green' : isDisconnected ? 'kpi-badge badge-rose' : statusData?.error_count ? 'kpi-badge badge-amber' : 'kpi-badge'} id="error-badge">
                    {errEval.isGreen ? 'ZERO_ERROR' : isDisconnected ? 'DISCONNECTED' : statusData?.error_count ? 'HAS_ERRORS' : 'N/A'}
                  </span>
                </div>
                <div className="kpi-desc">
                  <span>最近报错: </span>
                  <span
                    id="last-error-text"
                    style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}
                  >
                    {isDisconnected
                      ? '遥测通道中断 (Disconnected)'
                      : statusData?.last_error
                      ? statusData.last_error
                      : telemetryState === 'fresh'
                      ? '无 (Clean)'
                      : '--'}
                  </span>
                </div>
              </div>
            </div>

            {/* Hardware Resource Gauges (DCH-18) */}
            <div className="resource-grid">
              {/* CPU */}
              <div className="resource-card">
                <div className="resource-header">
                  <span className="resource-title">海外主机 CPU 算力负荷</span>
                  <span
                    className={cpuPct === null ? 'kpi-badge' : cpuPct < 70 ? 'kpi-badge badge-green' : cpuPct < 85 ? 'kpi-badge badge-amber' : 'kpi-badge badge-rose'}
                    id="cpu-status-badge"
                  >
                    {cpuPct === null ? '--' : cpuPct < 70 ? 'HEALTHY' : cpuPct < 85 ? 'WARNING' : 'DANGER'}
                  </span>
                </div>
                <div className="resource-value-row">
                  <span className="resource-val" id="cpu-val">
                    {cpuPct !== null ? `${cpuPct.toFixed(1)}%` : '--%'}
                  </span>
                  <span
                    style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}
                    id="cpu-load-val"
                  >
                    1m 负载: {loadAvg1m !== null ? Number(loadAvg1m).toFixed(2) : '--'}
                  </span>
                </div>
                <div className="resource-bar-track">
                  <div
                    className="resource-bar-fill"
                    id="cpu-bar"
                    style={{
                      width: `${cpuPct !== null ? Math.min(100, Math.max(0, cpuPct)) : 0}%`,
                      backgroundColor: cpuPct === null ? 'var(--border-subtle)' : cpuPct < 60 ? 'var(--color-emerald)' : 'var(--color-amber)',
                    }}
                  ></div>
                </div>
                <div className="resource-footer">
                  <span>Linux /proc/stat 真实采样</span>
                  <span id="cpu-cores-text">多核负载均衡</span>
                </div>
              </div>

              {/* RAM */}
              <div className="resource-card">
                <div className="resource-header">
                  <span className="resource-title">系统与容器内存</span>
                  <span
                    className={ramPct === null ? 'kpi-badge' : ramPct < 75 ? 'kpi-badge badge-green' : ramPct < 90 ? 'kpi-badge badge-amber' : 'kpi-badge badge-rose'}
                    id="ram-status-badge"
                  >
                    {ramPct === null ? '--' : ramPct < 75 ? 'HEALTHY' : ramPct < 90 ? 'WARNING' : 'DANGER'}
                  </span>
                </div>
                <div className="resource-value-row">
                  <span className="resource-val" id="ram-val">
                    {ramPct !== null ? `${ramPct.toFixed(1)}%` : '--%'}
                  </span>
                  <span
                    style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}
                    id="ram-rss-val"
                  >
                    容器 RSS: {rssMb !== null ? `${rssMb} MB` : '-- MB'}
                  </span>
                </div>
                <div className="resource-bar-track">
                  <div
                    className="resource-bar-fill"
                    id="ram-bar"
                    style={{
                      width: `${ramPct !== null ? Math.min(100, Math.max(0, ramPct)) : 0}%`,
                      backgroundColor: ramPct === null ? 'var(--border-subtle)' : ramPct < 70 ? 'var(--color-emerald)' : 'var(--color-amber)',
                    }}
                  ></div>
                </div>
                <div className="resource-footer">
                  <span>/proc/meminfo + cgroup</span>
                  <span>守护进程内存受控</span>
                </div>
              </div>

              {/* HDD / SSD */}
              <div className="resource-card">
                <div className="resource-header">
                  <span className="resource-title">磁盘存储空间</span>
                  <span
                    className={diskPct === null ? 'kpi-badge' : diskPct < 80 ? 'kpi-badge badge-green' : diskPct < 90 ? 'kpi-badge badge-amber' : 'kpi-badge badge-rose'}
                    id="disk-status-badge"
                  >
                    {diskPct === null ? '--' : diskPct < 80 ? 'HEALTHY' : diskPct < 90 ? 'WARNING' : 'DANGER'}
                  </span>
                </div>
                <div className="resource-value-row">
                  <span className="resource-val" id="disk-val">
                    {diskPct !== null ? `${diskPct.toFixed(1)}%` : '--%'}
                  </span>
                  <span
                    style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}
                    id="disk-avail-val"
                  >
                    根分区容量受控
                  </span>
                </div>
                <div className="resource-bar-track">
                  <div
                    className="resource-bar-fill"
                    id="disk-bar"
                    style={{
                      width: `${diskPct !== null ? Math.min(100, Math.max(0, diskPct)) : 0}%`,
                      backgroundColor: diskPct === null ? 'var(--border-subtle)' : diskPct < 75 ? 'var(--color-emerald)' : 'var(--color-amber)',
                    }}
                  ></div>
                </div>
                <div className="resource-footer">
                  <span>SQLite WAL 历史回补库</span>
                  <span>持久化存储容量健康</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '10px',
                borderTop: '1px solid rgba(51, 65, 85, 0.4)',
              }}
            >
              <button
                className="btn-secondary"
                id="btn-cancel-sys"
                onClick={() => setIsModalOpen(false)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
